import React, { useState, useEffect, useCallback } from 'react';

// --- Icon Components (Inline SVGs for reliability) ---
const CheckCircleIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const AlertTriangleIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

const XCircleIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="15" y1="9" x2="9" y2="15"></line>
    <line x1="9" y1="9" x2="15" y2="15"></line>
  </svg>
);

const ActivityIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
  </svg>
);

const RefreshCwIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

const ChevronDownIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const ChevronUpIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="18 15 12 9 6 15"></polyline>
  </svg>
);

// --- API Configuration ---
// Configure your API endpoints here. If an endpoint is unavailable, the service will show 100% uptime.
const API_CONFIG = {
  // Base URL for your status API (leave empty to use fallback data)
  baseUrl: process.env.REACT_APP_STATUS_API_URL || '',

  // Endpoints for different data
  endpoints: {
    services: '/api/services',
    incidents: '/api/incidents',
  },

  // Timeout for API requests (ms)
  timeout: 5000,

  // Refresh interval (ms)
  refreshInterval: 60000,
};

// --- Default Fallback Data (100% uptime when API unavailable) ---
const getDefaultServices = () => [
  { id: 1, name: 'Dashboard Application', status: 'operational', uptime: 100 },
  { id: 2, name: 'Authentication API', status: 'operational', uptime: 100 },
  { id: 3, name: 'Data Ingestion Engine', status: 'operational', uptime: 100 },
  { id: 4, name: 'Public Website', status: 'operational', uptime: 100 },
];

const getDefaultIncidents = () => [];

// --- API Fetching Utilities ---
const fetchWithTimeout = async (url, timeout = 5000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

const fetchServices = async () => {
  if (!API_CONFIG.baseUrl) {
    return getDefaultServices();
  }

  try {
    const data = await fetchWithTimeout(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.services}`,
      API_CONFIG.timeout
    );
    return data;
  } catch (error) {
    console.log('Services API unavailable, using fallback (100% uptime):', error.message);
    return getDefaultServices();
  }
};

const fetchIncidents = async () => {
  if (!API_CONFIG.baseUrl) {
    return getDefaultIncidents();
  }

  try {
    const data = await fetchWithTimeout(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.incidents}`,
      API_CONFIG.timeout
    );
    return data;
  } catch (error) {
    console.log('Incidents API unavailable, using fallback (no incidents):', error.message);
    return getDefaultIncidents();
  }
};

// --- Sub-components ---

const StatusBadge = ({ status }) => {
  let colorClass = "";
  let Icon = null;
  let text = "";

  switch (status) {
    case 'operational':
      colorClass = "text-green-600 bg-green-50 border-green-200";
      Icon = CheckCircleIcon;
      text = "Operational";
      break;
    case 'degraded':
      colorClass = "text-yellow-600 bg-yellow-50 border-yellow-200";
      Icon = AlertTriangleIcon;
      text = "Degraded Performance";
      break;
    case 'outage':
      colorClass = "text-red-600 bg-red-50 border-red-200";
      Icon = XCircleIcon;
      text = "Major Outage";
      break;
    default:
      colorClass = "text-gray-600 bg-gray-50 border-gray-200";
      Icon = ActivityIcon;
      text = "Unknown";
  }

  return (
    <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${colorClass}`}>
      <Icon className="w-4 h-4" />
      {text}
    </span>
  );
};

const UptimeBar = ({ status, uptime }) => {
  // Generate a 60-day history bar visual based on status
  const days = 60;
  const bars = Array.from({ length: days }, (_, i) => {
    // All bars green for operational/100% uptime
    if (status === 'operational' || uptime === 100) return 'bg-green-400';

    // Show degraded status in last few days
    if (status === 'degraded' && i > days - 3) return 'bg-yellow-400';

    // Show outage status in last few days
    if (status === 'outage' && i > days - 2) return 'bg-red-400';

    return 'bg-green-400';
  });

  return (
    <div className="flex gap-[2px] mt-3 h-8 items-end opacity-80 hover:opacity-100 transition-opacity" title="60-day uptime history">
      {bars.map((color, idx) => (
        <div key={idx} className={`w-full rounded-sm ${color} h-full`}></div>
      ))}
    </div>
  );
};

const IncidentCard = ({ incident, isFirst }) => {
  const [expanded, setExpanded] = useState(isFirst);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden mb-4 bg-white shadow-sm">
      <div
        className="p-4 flex items-center justify-between cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
            <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider w-fit
              ${incident.status === 'Resolved' ? 'bg-green-100 text-green-700' :
                incident.status === 'Monitoring' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
              {incident.status}
            </span>
            <h3 className="font-semibold text-slate-800">{incident.title}</h3>
        </div>
        <div className="flex items-center gap-4 text-sm text-slate-500">
          <span>{incident.date}</span>
          {expanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
        </div>
      </div>

      {expanded && (
        <div className="p-4 border-t border-slate-100 bg-white">
          <div className="space-y-6 relative pl-4 border-l-2 border-slate-200 ml-2">
            {incident.updates.map((update, idx) => (
              <div key={idx} className="relative">
                <div className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-slate-300 border-2 border-white"></div>
                <p className="text-sm text-slate-500 font-medium mb-1">{update.time}</p>
                <p className="text-slate-700 leading-relaxed">{update.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

// --- Main App Component ---

const App = () => {
  const [services, setServices] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [overallStatus, setOverallStatus] = useState('operational');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [loading, setLoading] = useState(true);

  // Fetch all data
  const loadData = useCallback(async () => {
    try {
      const [servicesData, incidentsData] = await Promise.all([
        fetchServices(),
        fetchIncidents(),
      ]);

      setServices(servicesData);
      setIncidents(incidentsData);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading data:', error);
      // On error, use defaults (100% uptime)
      setServices(getDefaultServices());
      setIncidents(getDefaultIncidents());
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => loadData(), API_CONFIG.refreshInterval);
    return () => clearInterval(interval);
  }, [loadData]);

  // Determine overall status based on services
  useEffect(() => {
    if (services.length === 0) return;

    const hasOutage = services.some(s => s.status === 'outage');
    const hasDegraded = services.some(s => s.status === 'degraded');

    if (hasOutage) setOverallStatus('outage');
    else if (hasDegraded) setOverallStatus('degraded');
    else setOverallStatus('operational');
  }, [services]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-slate-500">Loading status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans pb-12">

      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-bold text-lg tracking-tight text-slate-900">Bay Security</span>
            <span className="hidden sm:inline-block h-4 w-[1px] bg-slate-300 mx-2"></span>
            <span className="text-slate-500 text-sm hidden sm:inline-block">System Status</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">

        {/* Hero Status Banner */}
        <div className={`rounded-lg shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition-colors duration-300
          ${overallStatus === 'operational' ? 'bg-green-600 text-white' :
            overallStatus === 'degraded' ? 'bg-yellow-500 text-white' : 'bg-red-600 text-white'}`}>

          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-full backdrop-blur-sm">
              {overallStatus === 'operational' && <CheckCircleIcon className="w-8 h-8" />}
              {overallStatus === 'degraded' && <AlertTriangleIcon className="w-8 h-8" />}
              {overallStatus === 'outage' && <XCircleIcon className="w-8 h-8" />}
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {overallStatus === 'operational' ? 'All Systems Operational' :
                 overallStatus === 'degraded' ? 'Partial System Outage' : 'Major System Outage'}
              </h1>
              <p className="text-white/90 mt-1 text-sm">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-slate-800 mb-6">Current Status by Service</h2>
          <div className="grid gap-4">
            {services.map((service) => (
              <div key={service.id} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-lg text-slate-800">{service.name}</span>
                    <div className="group relative">
                        <span className="cursor-help text-xs text-slate-400 border border-slate-200 px-1.5 rounded hover:bg-slate-50">?</span>
                        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity">
                            {service.uptime}% Uptime (90 days)
                        </span>
                    </div>
                  </div>
                  <StatusBadge status={service.status} />
                </div>
                {/* Uptime Visual */}
                <UptimeBar status={service.status} uptime={service.uptime} />
                <div className="flex justify-between text-xs text-slate-400 mt-2 font-medium">
                  <span>60 days ago</span>
                  <span className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">{service.uptime}% uptime</span>
                  <span>Today</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Incident History */}
        <section>
          <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-3">
            Incident History
            <span className="text-sm font-normal text-slate-500 bg-slate-100 px-2 py-1 rounded-full">Last 30 Days</span>
          </h2>

          {incidents.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
              <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">No incidents reported</p>
              <p className="text-slate-400 text-sm mt-1">All systems have been operating normally</p>
            </div>
          ) : (
            <div className="space-y-4">
              {incidents.map((incident, index) => (
                <IncidentCard key={incident.id} incident={incident} isFirst={index === 0} />
              ))}
            </div>
          )}

          {incidents.length > 0 && (
            <div className="mt-8 text-center">
              <button className="text-blue-600 font-medium text-sm hover:underline">
                View older incidents &rarr;
              </button>
            </div>
          )}
        </section>

      </main>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto px-6 py-8 mt-12 border-t border-slate-200 text-center">
        <div className="flex justify-center items-center gap-2 mb-4 text-slate-400">
           <span className="font-semibold text-slate-500">Bay Security</span>
        </div>
        <div className="text-sm text-slate-500 space-x-6">
          <a href="https://bay.security" className="hover:text-blue-600 transition-colors">Support</a>
          <a href="https://bay.security" className="hover:text-blue-600 transition-colors">Privacy Policy</a>
          <a href="https://bay.security" className="hover:text-blue-600 transition-colors">Terms of Service</a>
        </div>
        <p className="text-xs text-slate-400 mt-6">
          &copy; {new Date().getFullYear()} Bay AI Inc.
        </p>
      </footer>

    </div>
  );
};

export default App;
